import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BreadcrumbGroup, {
  BreadcrumbGroupProps,
} from "@amzn/awsui-components-react/polaris/breadcrumb-group";

import { TAB_ID_KEY, getSearchForNavigation } from "../../util/urlUtils";
import {
  LEFT_NAV_LABELS,
  LeftNavPath,
  resolveLeftNavPath,
} from "./shellRoutes";

/** App Shell breadcrumbs. Two-item trail: root + active section. */

const ROOT_BREADCRUMB_TEXT = "Devices ASIN Central";
const ROOT_BREADCRUMB_HREF = "/";
const VALIDATION_PATH: LeftNavPath = "/validation";

export const AppBreadcrumbs: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const activePath = useMemo<LeftNavPath>(
    () => resolveLeftNavPath(location.pathname),
    [location.pathname]
  );

  const items = useMemo<ReadonlyArray<BreadcrumbGroupProps.Item>>(
    () => [
      { text: ROOT_BREADCRUMB_TEXT, href: ROOT_BREADCRUMB_HREF },
      { text: LEFT_NAV_LABELS[activePath], href: activePath },
    ],
    [activePath]
  );

  const handleFollow = useCallback(
    (
      event: CustomEvent<
        BreadcrumbGroupProps.ClickDetail<BreadcrumbGroupProps.Item>
      >
    ) => {
      event.preventDefault();
      // Preserve `hash` (the serialized Applied Filter). Strip `tabId`
      // when the destination isn't `/validation` since it's a
      // Validation-only param.
      const isValidationDestination = event.detail.href === VALIDATION_PATH;
      const search = getSearchForNavigation(
        isValidationDestination ? [] : [TAB_ID_KEY]
      );
      navigate({
        pathname: event.detail.href,
        search,
        hash: window.location.hash,
      });
    },
    [navigate]
  );

  return (
    <BreadcrumbGroup
      // Spread to satisfy Cloudscape's mutable-array typing.
      items={[...items]}
      onFollow={handleFollow}
      expandAriaLabel="Show path"
      ariaLabel="Breadcrumbs"
    />
  );
};

export default AppBreadcrumbs;
